import type * as React from 'react'
import { useMemo } from 'react'
import type { JSX } from 'react'

import type { ErrorLike } from '@apollo/client'
import clsx from 'clsx'

import {
  CalendarDaysIcon,
  Flex,
  Heading,
  IconButton,
  InfoIcon,
  Popover,
  SendHorizontalIcon,
  Skeleton,
  Text,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { normalizeNetworkName } from '~publish/helpers/channels'

import type { DifferenceMode } from '../../graphqlEnums'
import { MetricCard } from './MetricCard'
import { metricToStat } from './metricToStat'
import { pickShareableStats } from './pickShareableStats'
import { ChartErrorState } from '../ChartErrorState'
import { NoDataEmptyState } from '../NoDataEmptyState'
import { NeverSyncedEmptyState } from '../NeverSyncedEmptyState'
import {
  comparisonShortLabel,
  formatDateRange,
  formatInsightsDateRange,
  getComparisonLengthNotice,
  getNoDataDescription,
} from '../../utils'
import type { ComparisonMode } from '../../utils'
import { SectionCard } from '../SectionCard'
import { useExportSummaryAsPost } from '../../hooks/useExportSummaryAsPost'
import { captureSummaryAsBlob } from '../../summaryCardToImage'
import type { CaptureSummaryOptions } from '../../summaryCardToImage'
import { useRegisterSnapshot } from '../../context'

import sectionButtonStyles from '../sectionActionButton.module.css'
import styles from './SummaryMetrics.module.css'

export interface SummaryMetricItem {
  type: string
  unit: string
  name?: string
  /** @deprecated Use `name` instead. Kept for backward compatibility with PostInsightsSummaryMetric. */
  displayName?: string
  description: string
  value: number
  difference?: number | null
  differenceMode?: DifferenceMode
}

/**
 * Context for the "Share Summary" image + composer prefill. When omitted,
 * the share button is hidden.
 */
export type SummaryShareContext =
  | {
      /** Single-channel Insights page: card shows the channel avatar + service icon; composer targets the channel. */
      kind: 'channel'
      channelId: string
      channelName: string
      /** Raw service identifier (e.g. "instagram") — used to derive a label. */
      channelService: string
      /** URL of the channel's profile image. Rendered as a circle on the card. */
      channelAvatarUrl?: string
    }
  | {
      /** AllChannels page: card shows the account avatar (or none) and a "{n} channels" subtitle; composer targets channelIds (or every connected channel when omitted). */
      kind: 'allChannels'
      /**
       * Channel IDs the composer should target. Omit to open the composer
       * with all connected channels selected.
       */
      channelIds?: string[]
      /**
       * Total connected channels — used to caption the snapshot subtitle
       * (`"{n} channels · {date range}"`) when `channelIds` is omitted.
       */
      totalChannelCount: number
    }

interface SummaryMetricsContentProps {
  metrics: SummaryMetricItem[]
  loading: boolean
  error: ErrorLike | undefined
  skeletonCount?: number
  hasNeverSynced?: boolean
  selectedDateRange?: string
  hasChannelFilter?: boolean
  onClearChannelFilter?: () => void
  refreshing?: boolean
}

interface SummaryMetricsProps extends SummaryMetricsContentProps {
  /** Heading level for the Summary section within its surrounding page. */
  headingAs?: 'h2' | 'h3'
  /**
   * Comparison window (inclusive start + end). Drives the "Compared to ..."
   * subtitle and the length-mismatch warning. Omit on surfaces with no
   * comparison.
   */
  comparisonRange?: { startDate: Date; endDate: Date }
  /**
   * Active comparison mode. Used to caption the per-tile delta in the
   * shareable snapshot (e.g. "vs prev. month"). Defaults to `previous-period`.
   */
  comparisonMode?: ComparisonMode
  startDate: Date
  endDate: Date
  headerActions?: React.ReactNode
  refreshing?: boolean
  shareContext?: SummaryShareContext
  /** When false, hides the "Share as Post" button (sharing opens the composer). */
  canCreatePosts?: boolean
  className?: string
}

export const SummaryMetricsContent = ({
  metrics,
  loading,
  error,
  skeletonCount = 8,
  hasNeverSynced,
  selectedDateRange,
  hasChannelFilter,
  onClearChannelFilter,
  refreshing,
}: SummaryMetricsContentProps): JSX.Element => {
  if (loading) {
    const skeletonSlots = Array.from(
      { length: skeletonCount },
      (_, slotIndex) => `summary-metric-${slotIndex + 1}`,
    )
    return (
      <ul className={styles.grid}>
        {skeletonSlots.map((slot) => (
          <li key={slot}>
            <Skeleton className={styles.skeletonCard} />
          </li>
        ))}
      </ul>
    )
  }

  if (error) {
    return <ChartErrorState size="small" />
  }

  if (metrics.length === 0) {
    if (hasNeverSynced) {
      return <NeverSyncedEmptyState />
    }
    return (
      <NoDataEmptyState
        description={getNoDataDescription(selectedDateRange, hasChannelFilter)}
        showSuggestion
        onClearChannelFilter={onClearChannelFilter}
      />
    )
  }

  return (
    <ul
      className={clsx(styles.grid, refreshing && styles.refreshing)}
      aria-busy={refreshing}
    >
      {metrics.map((metric) => (
        <li key={metric.type}>
          <MetricCard metric={metric} />
        </li>
      ))}
    </ul>
  )
}

export function SummaryMetrics({
  metrics,
  loading,
  error,
  headingAs = 'h2',
  comparisonRange,
  comparisonMode = 'previous-period',
  startDate,
  endDate,
  skeletonCount,
  hasNeverSynced,
  headerActions,
  selectedDateRange,
  hasChannelFilter,
  onClearChannelFilter,
  refreshing,
  shareContext,
  canCreatePosts = true,
  className,
}: SummaryMetricsProps): JSX.Element {
  const comparisonNotice = getComparisonLengthNotice(
    startDate,
    endDate,
    comparisonRange?.startDate,
    comparisonRange?.endDate,
    selectedDateRange,
  )

  // Rich subtitle (info popover + warning text) only when the periods aren't
  // directly comparable; otherwise the plain date-range string below.
  const subtitle = comparisonNotice ? (
    <Flex align="center" gap="2xs" className={styles.comparisonNotice}>
      <Popover>
        <Popover.Trigger>
          <IconButton
            type="button"
            label="About comparison"
            size="small"
            variant="tertiary"
          >
            <InfoIcon color="subtle" size="xsmall" />
          </IconButton>
        </Popover.Trigger>
        <Popover.Content
          side="top"
          align="start"
          arrow
          className={styles.popoverContent}
        >
          <Flex direction="column" gap="xs">
            <Heading size="sm" as="h2" icon={<CalendarDaysIcon size="small" />}>
              Comparison
            </Heading>
            <Text color="subtle">
              These periods have different lengths, so metrics may not be
              directly comparable.
            </Text>
          </Flex>
        </Popover.Content>
      </Popover>
      <Text
        as="span"
        size="sm"
        color="subtle"
        className={styles.comparisonNoticeText}
      >
        Comparing {comparisonNotice.primaryDays} vs{' '}
        {comparisonNotice.comparisonDays} days ·{' '}
        {formatDateRange(startDate, endDate)}
      </Text>
    </Flex>
  ) : undefined

  const dateRange = comparisonNotice
    ? undefined
    : formatInsightsDateRange(
        startDate,
        endDate,
        comparisonRange?.startDate,
        comparisonRange?.endDate,
      )

  const composerChannelIds =
    shareContext?.kind === 'channel'
      ? [shareContext.channelId]
      : shareContext?.kind === 'allChannels'
        ? shareContext.channelIds
        : undefined

  const { onShare, isSharing } = useExportSummaryAsPost({
    channelIds: composerChannelIds,
  })

  const { account } = useAccount()

  // The visual snapshot input, shared by "Share as Post" and the bulk snapshot
  // download so both render an identical image. Composer-only text/alt-text is
  // layered on in handleShareSummary.
  const summaryCaptureOptions = useMemo<CaptureSummaryOptions | null>(() => {
    if (!shareContext) return null
    const stats = pickShareableStats(metrics).map(metricToStat)
    const dateRangeLabel = formatDateRange(startDate, endDate)
    const comparisonLabel = comparisonShortLabel(comparisonMode)

    if (shareContext.kind === 'channel') {
      return {
        channelName: shareContext.channelName,
        channelService: shareContext.channelService,
        channelAvatarUrl: shareContext.channelAvatarUrl,
        dateRangeLabel,
        comparisonLabel,
        stats,
      }
    }

    const channelCount =
      shareContext.channelIds?.length ?? shareContext.totalChannelCount
    const channelCountLabel = `${channelCount} ${
      channelCount === 1 ? 'channel' : 'channels'
    }`
    return {
      channelAvatarUrl: account?.avatar ?? undefined,
      dateRangeLabel,
      channelCountLabel,
      comparisonLabel,
      stats,
    }
  }, [
    shareContext,
    metrics,
    startDate,
    endDate,
    comparisonMode,
    account?.avatar,
  ])

  const hasShareableSummary =
    Boolean(shareContext) && !loading && !error && metrics.length > 0

  useRegisterSnapshot({
    id: 'summary',
    filename: 'summary.png',
    order: 0,
    available: hasShareableSummary && !refreshing,
    getBlob: async () => {
      if (!summaryCaptureOptions) {
        throw new Error('Summary snapshot is not available')
      }
      const { blob } = await captureSummaryAsBlob(summaryCaptureOptions)
      return blob
    },
  })

  const handleShareSummary = (): void => {
    if (!shareContext || !summaryCaptureOptions) return
    const { dateRangeLabel, comparisonLabel, stats } = summaryCaptureOptions
    const statSummary = stats
      .map(
        (s) =>
          `${s.label}: ${s.value}${
            s.differenceLabel && s.differenceDirection
              ? ` (${s.differenceDirection === 'up' ? 'up' : 'down'} ${
                  s.differenceLabel
                } ${comparisonLabel})`
              : ''
          }`,
      )
      .join('; ')

    if (shareContext.kind === 'channel') {
      const serviceLabel = normalizeNetworkName(shareContext.channelService)
      void onShare({
        ...summaryCaptureOptions,
        text: `Here's a snapshot of my ${serviceLabel} performance! 📊`,
        altText: `Buffer Insights snapshot for ${shareContext.channelName} on ${serviceLabel} (${dateRangeLabel}). ${statSummary}.`,
      })
      return
    }

    void onShare({
      ...summaryCaptureOptions,
      text: `Here's a snapshot of my performance across my channels! 📊`,
      altText: `Buffer Insights snapshot across ${
        summaryCaptureOptions.channelCountLabel ?? ''
      } (${dateRangeLabel}). ${statSummary}.`,
    })
  }

  const combinedHeaderActions = (
    <Flex direction="row" gap="xs" align="center">
      {hasShareableSummary && canCreatePosts && (
        <IconButton
          className={sectionButtonStyles.neutralBackground}
          size="medium"
          onClick={handleShareSummary}
          loading={isSharing}
          disabled={refreshing}
          label={isSharing ? 'Sharing Summary…' : 'Share as Post for Summary'}
          tooltip={isSharing ? 'Sharing…' : 'Share as Post'}
        >
          <SendHorizontalIcon />
        </IconButton>
      )}
      {headerActions}
    </Flex>
  )

  return (
    <SectionCard className={className}>
      <SectionCard.Header>
        <SectionCard.Title as={headingAs}>Summary</SectionCard.Title>
        <SectionCard.Actions>{combinedHeaderActions}</SectionCard.Actions>
        <SectionCard.Subtitle
          className={comparisonNotice ? styles.comparisonSubtitle : undefined}
        >
          {subtitle ?? dateRange}
        </SectionCard.Subtitle>
      </SectionCard.Header>
      <SummaryMetricsContent
        metrics={metrics}
        loading={loading}
        error={error}
        skeletonCount={skeletonCount}
        hasNeverSynced={hasNeverSynced}
        selectedDateRange={selectedDateRange}
        hasChannelFilter={hasChannelFilter}
        onClearChannelFilter={onClearChannelFilter}
        refreshing={refreshing}
      />
    </SectionCard>
  )
}
